import React, { useState, useContext } from 'react';
import { func } from 'prop-types';
import {
  CircularProgress,
  Button,
  InputLabel,
  Select,
  MenuItem,
} from '@nextiva/next-ui-components';
import { useMutation, useQueryClient } from 'react-query';
import {
  UserContext,
  actionTypes,
} from 'containers/pages/ManageUsers/usersContext';
import { useRootUserList } from 'customHooks/userCustomHooks';
import { ChipGroup } from 'components/ChipGroup';
import { useSnackbar } from 'hooks';
import { toggleState } from 'utils/state-helpers';
import { userTeamMapping } from 'services/TOS';
import { FeatureContext } from 'components/AccessResolvers/featureContext';
import {
  updateSelectedUserList,
  updateUserListTeams,
  checkAllUserLists,
} from './addTeamUtils';

const AddTeam = ({ toggleDrawer }) => {
  const {
    dispatch,
    userState: { selectedUserList, baseUserList, userList, teamsOptions },
  } = useContext(UserContext);
  const { corpAcctNbr } = useContext(FeatureContext);

  const [selectedUsers, setSelectedUsers] = useState(selectedUserList);
  const [teamName, setTeamName] = useState('');
  const [teamKey, setTeamKey] = useState('');
  const [sending, setSending] = useState(false);
  const [pristine, setPristine] = useState(true);
  const [showOpened, setShowOpened] = useState(false);

  const setSnackbar = useSnackbar(true);

  const { updateRootUserList } = useRootUserList();
  const updateUserTeamMapping = useMutation(userTeamMapping);
  const queryClient = useQueryClient();

  const optimisticallyUpdateUsers = () => {
    const updatedSelectedUsers = updateSelectedUserList(
      selectedUsers,
      teamsOptions,
      teamName,
    );

    return {
      baseUserList: updateUserListTeams(updatedSelectedUsers, baseUserList),
      userList: updateUserListTeams(updatedSelectedUsers, userList),
    };
  };

  const resetCheckboxes = () => {
    const updatedLists = optimisticallyUpdateUsers();

    dispatch({
      type: actionTypes.SET_USER_LIST,
      userList: checkAllUserLists(updatedLists.userList, false),
    });

    dispatch({
      type: actionTypes.SET_BASE_USER_LIST,
      userList: checkAllUserLists(updatedLists.baseUserList, false),
    });
  };

  const handleRemove = (email) => {
    if (selectedUsers.length === 1) toggleDrawer();
    setSelectedUsers(selectedUsers.filter((user) => user.email !== email));
  };

  const onSelectChange = (e) => {
    setTeamName(teamsOptions.find((team) => team.key === e.target.value).label);
    setTeamKey(e.target.value);
    setPristine(false);
  };

  const closeActionBar = () => {
    dispatch({
      type: actionTypes.SET_TABLE_HEADER_STATE,
      tableHeaderState: false,
    });
  };

  const onSave = () => {
    const mappedTeamUser = [];
    selectedUsers.forEach((user) => {
      mappedTeamUser.push({
        op: 'add',
        path: `/${teamKey}/members/-`,
        value: `${user.nextiva_uuid ?? user.nextivaUuid}`,
      });
    });

    setSending(true);
    setPristine(true);

    updateUserTeamMapping
      .mutateAsync({
        corpAcctNbr,
        body: mappedTeamUser,
      })
      .then(() => {
        setSending(false);
        updateRootUserList();
        setSnackbar({
          message: `The ${teamName} team has been updated!`,
          severity: 'success',
        });
        setTimeout(() => {
          toggleDrawer();
          resetCheckboxes();
          closeActionBar();
        }, 2000);
        queryClient.invalidateQueries(['getTeams']);
      })
      .catch(() => {
        setSnackbar({
          message: 'Teams failed to update! Please, contact support.',
          severity: 'error',
        });
      });
  };

  const chipList = selectedUsers.map((user) => {
    return {
      key: user.email,

      label: `${user.first_name ?? user.firstName} ${
        user.last_name ?? user.lastName
      }`,

      onDelete: handleRemove,
    };
  });

  const teamOptionsMenuItems = teamsOptions.map(({ key, label }) => ({
    value: key,
    key,
    label,
  }));

  return (
    <div className="m-add-team">
      <div className="m-add-team__container">
        <div className="m-add-team__users">selected users</div>
        <div className="m-add-team__chips">
          <ChipGroup
            chipList={chipList}
            open={showOpened}
            showClick={toggleState(setShowOpened)}
            className="editTeam"
          />
        </div>
        <InputLabel id="Team-Option">TEAM</InputLabel>
        <Select
          labelId="Team-Option"
          onChange={onSelectChange}
          value={teamKey}
          fullWidth
          id="team"
          data-testid="PlatformAdminUI-AddTeam-SelectTeam"
        >
          {teamOptionsMenuItems.map((option) => (
            <MenuItem key={option.key} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </div>
      <div className="m-drawer-footer">
        <div className="m-drawer-footer__container">
          <div className="m-drawer-footer__container__action-bar">
            <div className="m-drawer-footer__container__action-bar__button">
              <Button
                id="cancel-drawer"
                className="footer__btn"
                type="button"
                color="secondary"
                variant="contained"
                onClick={toggleDrawer}
                fullWidth
                data-testid="PlatformAdminUI-AddTeam-CancelButton"
              >
                Cancel
              </Button>
            </div>
            <div className="m-drawer-footer__container__action-bar__button">
              <Button
                type="button"
                fullWidth
                color="primary"
                variant="contained"
                onClick={onSave}
                className="footer__btn"
                disabled={pristine}
                data-testid="PlatformAdminUI-AddTeam-SaveButton"
              >
                Save
              </Button>
            </div>
            {sending && (
              <div className="m-drawer-footer__container__action-bar__spinner">
                <CircularProgress
                  style={{ color: '#009FE0' }}
                  variant="indeterminate"
                  size={36}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

AddTeam.propTypes = {
  toggleDrawer: func.isRequired,
};

export default AddTeam;
